import type { SearchRequest, SearchResult } from './listingPages';
import type { PropertyListSection } from '~/components/PropertyList';
import type { PropertyFilterPatch } from '~/core/entities/property/filterDefinitions';

import { useLogger } from '~/composables/useLogger';
import { copyPropertyFilters } from '~/core/entities/property/filterPolicy';
import { EventBusKeys } from '~/core/helpers/symbols';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

import { isSameUrl } from './actions';
import { usePropertySearchScope } from './context';
import {
	emptySearchResult,
	fetchListingPages,
	LISTING_PAGE_SIZE
} from './listingPages';
import { filterRequestKey, filterStateKey } from './store';

const MISSING_SCOPE = 'Property search requires its route scope';

export function usePropertySearch(
	options: {
		includeH3Clusters?: boolean;
		config?: {
			shouldScrollToTopOnFiltersChange?: boolean;
			scrollElement?: Ref<InstanceType<typeof PropertyListSection> | null>;
		};
	} = {}
) {
	const scope = usePropertySearchScope();
	const route = useRoute();
	// Created in setup: onError runs after an await, outside the Nuxt context.
	const logger = useLogger('PropertySearch');
	if (!scope) {
		logger.error(MISSING_SCOPE, { route: route.fullPath });
		throw new Error(MISSING_SCOPE);
	}
	const actions = scope.actions;
	const api = scope.filtersApi;
	const search = scope.search;
	const viewMoreBus = useEventBus<string>(
		scope.eventKey(EventBusKeys.viewMoreClicked)
	);
	const router = useRouter();
	const requestEvent = import.meta.server ? useRequestEvent() : undefined;
	const cacheControl = import.meta.server
		? useResponseHeader('Cache-Control')
		: undefined;
	// PP-2011: an SRP without results answers 404 so crawlers stop flagging soft 404s.
	// no-store keeps the CDN from serving that 404 for 24h after listings arrive.
	function markSearchWithoutResults() {
		if (!requestEvent) return;
		setResponseStatus(requestEvent, 404);
		if (cacheControl) cacheControl.value = 'no-store';
	}
	const { user } = useUserSession();
	const getCookieData = useCookieData();
	const pageFromUrl = () => {
		const page = Number(route.query.page);
		return Number.isSafeInteger(page) && page > 0 ? page : 1;
	};
	if (!useNuxtApp().isHydrating || search.revision.value === 0)
		search.restore(
			api.getFiltersFromPath(route.fullPath),
			pageFromUrl(),
			route.fullPath
		);
	search.setNavigation({
		build: selected =>
			api.getPathFromFilters(selected, {
				regions: selected.regions,
				cities: selected.cities,
				states: selected.states,
				macroregions: selected.macroregions
			}),
		push: url => router.push(url),
		currentUrl: () => route.fullPath,
		onError: (url, error) =>
			logger.warn('Filter navigation was rejected', {
				url,
				error: error instanceof Error ? error.message : String(error)
			})
	});
	const state = useState<SearchResult>(
		scope.stateKey('result'),
		emptySearchResult
	);
	let plan: SearchRequest = {
		filters: api.getFiltersFromPath(route.fullPath),
		page: pageFromUrl(),
		mode: 'replace'
	};
	const requestKey = (request: SearchRequest) =>
		filterRequestKey({
			...api.prepareFiltersForRequest(request.filters, {
				includeH3Clusters: options.includeH3Clusters
			}),
			page: request.page
		});
	let lastRequestKey = requestKey(plan);
	let lastUrlFiltersKey = filterStateKey(plan.filters);
	let revision = 0;
	let disposed = false;
	let ownPaginationUrl: string | undefined;

	async function runSearch() {
		const currentRevision = ++revision;
		const searchRevision = search.revision.value;
		const request = { ...plan, filters: copyPropertyFilters(plan.filters) };
		const previous = state.value;
		const selected = api.prepareFiltersForRequest(request.filters, {
			includeH3Clusters: options.includeH3Clusters
		});
		const outcome = await fetchListingPages({
			request,
			previous,
			singlePage: !!selected.countPluscodes,
			isStale: () => disposed || currentRevision !== revision,
			fetchPage: page => fetchListingPage(selected, page)
		});
		if (outcome.status === 'failed')
			state.value = {
				...previous,
				failure: request.mode === 'append' ? 'page' : 'search'
			};
		// Only the status goes to the payload; the cards already travel in `state`.
		if (outcome.status !== 'done') return outcome.status;
		search.acceptResolvedLocation(outcome.resolvedFilters, searchRevision);
		state.value = outcome.result;
		if (!outcome.result.filteredCount) markSearchWithoutResults();
		return outcome.status;
	}
	async function fetchListingPage(
		selected: ReturnType<typeof api.prepareFiltersForRequest>,
		page: number
	) {
		const [result, error] = await nuxtPropertiesRepository.list(
			{
				...selected,
				...(!selected.countPluscodes
					? { page, perPage: LISTING_PAGE_SIZE }
					: {}),
				markUserWishlistsId: user.value?.id
			},
			getCookieData()
		);
		if (error || !result) return null;
		return {
			properties: result.properties,
			filteredCount: result.pagination.filteredCount,
			totalPages: result.pagination.totalPages,
			locationSlugFilters: result.locationSlugFilters
		};
	}
	const fetcher = useAsyncData(scope.stateKey('listing'), runSearch, {
		server: true
	});
	const isFetching = computed(() => fetcher.status.value === 'pending');
	const filters = search.filters;
	const properties = computed(() => state.value.properties);
	const displayPagination = computed(() => ({
		page: state.value.page,
		perPage: LISTING_PAGE_SIZE
	}));

	async function loadFromUrl() {
		const urlFilters = api.getFiltersFromPath(route.fullPath);
		const urlFiltersKey = filterStateKey(urlFilters);
		if (urlFiltersKey !== lastUrlFiltersKey) {
			search.restore(urlFilters, pageFromUrl(), route.fullPath);
			lastUrlFiltersKey = urlFiltersKey;
		}
		const nextPlan: SearchRequest = {
			filters: urlFilters,
			page: pageFromUrl(),
			mode: 'replace'
		};
		const key = requestKey(nextPlan);
		if (key === lastRequestKey && state.value.failure !== 'search') return;
		lastRequestKey = key;
		revision++;
		plan = nextPlan;
		state.value = emptySearchResult();
		const startedOn = route.fullPath;
		await fetcher.execute();
		if (
			!disposed &&
			startedOn === route.fullPath &&
			options.config?.shouldScrollToTopOnFiltersChange
		)
			await options.config.scrollElement?.value?.scrollToTop();
	}
	watch(
		() => route.fullPath,
		async path => {
			if (route.name !== scope.routeName) {
				revision++;
				return;
			}
			// The URL "ver mais" wrote: the cards are already appended.
			if (ownPaginationUrl && isSameUrl(path, ownPaginationUrl)) {
				lastRequestKey = requestKey({
					filters: api.getFiltersFromPath(path),
					page: pageFromUrl(),
					mode: 'replace'
				});
				ownPaginationUrl = undefined;
				return;
			}
			await loadFromUrl();
		}
	);
	function updateFiltersManager(patch: PropertyFilterPatch) {
		return search.patch(patch);
	}
	function clearFilters() {
		return actions.clear();
	}
	async function nextPage() {
		if (
			isFetching.value ||
			state.value.failure === 'search' ||
			state.value.page >= state.value.totalPages
		)
			return;
		viewMoreBus.emit('imoveis');
		const startedOn = route.fullPath;
		const targetPage = state.value.page + 1;
		plan = {
			filters: copyPropertyFilters(filters.value),
			page: targetPage,
			mode: 'append'
		};
		await fetcher.execute();
		// Writing the page now would cancel a newer gesture's navigation.
		if (
			disposed ||
			route.fullPath !== startedOn ||
			search.hasPendingNavigation() ||
			state.value.failure ||
			state.value.page !== targetPage
		)
			return;
		ownPaginationUrl = router.resolve({
			path: route.path,
			query: { ...route.query, page: targetPage }
		}).fullPath;
		await router.replace(ownPaginationUrl);
	}
	onScopeDispose(() => {
		disposed = true;
		revision++;
	});
	onMounted(() => {
		if (state.value.failure === 'search') void loadFromUrl();
	});
	return {
		filters,
		properties,
		displayPagination,
		isFetching,
		isFetchingForTheFirstTime: computed(
			() => isFetching.value && !properties.value.length
		),
		filteredCount: computed(() => state.value.filteredCount),
		hasProperties: computed(() => properties.value.length > 0),
		hasError: computed(() => state.value.failure !== null),
		hasLessThenTenProperties: computed(() => properties.value.length < 10),
		nextPage,
		updateFiltersManager,
		clearFilters
	};
}
